import { createElement, lazy, Suspense } from 'react';
import { CircleUserRound } from 'lucide-react';
import type { ComponentType } from 'react';
import type { FeatureModule, FeatureRoute } from '@/app/contract';
import { APP_ROUTES, mePath } from '@/app/paths';
import { registerNamespace } from '@/i18n';
import en from './locales/en.json';
import zh from './locales/zh-Hans.json';
import { presentAccounts, presentAddAccount } from './accounts/present';

registerNamespace('me', { en, 'zh-Hans': zh });
function screen(load: () => Promise<{ default: ComponentType }>) {
  const Component = lazy(load);
  return function Screen() { return createElement(Suspense, { fallback: null }, createElement(Component)); };
}
const pages: Array<[keyof typeof APP_ROUTES, ComponentType]> = [
  ['me', screen(() => import('./pages/MePage'))],
  ['meProfile', screen(() => import('./pages/ProfilePage'))],
  ['meNotifications', screen(() => import('./pages/NotificationsPage'))],
  ['mePrivacy', screen(() => import('./pages/PrivacyPages').then(m => ({ default: m.PrivacyPage })))],
  ['mePresence', screen(() => import('./pages/PrivacyPages').then(m => ({ default: m.PresencePage })))],
  ['mePresenceList', screen(() => import('./pages/PrivacyPages').then(m => ({ default: m.PresenceListPage })))],
  ['meChats', screen(() => import('./pages/LocalPages').then(m => ({ default: m.ChatsPage })))],
  ['meAppearance', screen(() => import('./pages/LocalPages').then(m => ({ default: m.AppearancePage })))],
  ['meLanguage', screen(() => import('./pages/LocalPages').then(m => ({ default: m.LanguagePage })))],
  ['meAccount', screen(() => import('./pages/AccountPage'))],
  ['meIdentifiers', screen(() => import('./pages/AccountPage').then(m => ({ default: m.IdentifiersPage })))],
  ['meDevices', screen(() => import('./pages/SecurityPages').then(m => ({ default: m.DevicesPage })))],
  ['meActivity', screen(() => import('./pages/SecurityPages').then(m => ({ default: m.ActivityPage })))],
  ['mePassword', screen(() => import('./pages/SecurityPages').then(m => ({ default: m.PasswordPage })))],
  ['meExport', screen(() => import('./pages/ExportPage'))],
  ['meAI', screen(() => import('./pages/AISettingsPage'))],
  ['meAbout', screen(() => import('./pages/AboutPage'))],
];
const feature: FeatureModule = {
  id: 'me', section: { id: 'me', titleKey: 'me:title', icon: CircleUserRound },
  routes: pages.map(([name, component]): FeatureRoute => ({ ...APP_ROUTES[name], component })),
  commands: [
    { id: 'me.addAccount', titleKey: 'me:accounts.add', run: presentAddAccount },
    { id: 'me.accounts', titleKey: 'me:accounts.title', run: presentAccounts },
    { id: 'me.settings', titleKey: 'me:commands.settings', shortcut: 'mod+,', run: nav => nav.open(mePath()) },
    ...(['profile', 'appearance', 'language', 'account'] as const).map(page => ({
      id: 'me.' + page, titleKey: 'me:commands.' + page, run: (nav: Parameters<NonNullable<FeatureModule['commands']>[number]['run']>[0]) => nav.open(mePath(page)),
    })),
  ],
};
export default feature;
export { MeRuntime } from './public';
